import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Table, Button, Space, Modal, message, Card, theme, Input, Form } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { INDUSTRY_API } from '../constants/api';
import axiosInstance from '../services/axiosConfig';

const { TextArea } = Input;

const IndustryForm = ({ visible, onCancel, onSubmit, initialValues, title }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible && initialValues) {
      form.setFieldsValue({
        name: initialValues.IndustryName,
        description: initialValues.Description,
        status: initialValues.Status
      });
    }
  }, [visible, initialValues, form]);

  const handleSubmit = () => {
    form.validateFields().then(values => {
      onSubmit(values);
      form.resetFields();
    });
  };

  return (
    <Modal
      visible={visible}
      title={title}
      onCancel={onCancel}
      onOk={handleSubmit}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          status: true,
          ...initialValues && {
            name: initialValues.IndustryName,
            description: initialValues.Description,
            status: initialValues.Status
          }
        }}
      >
        <Form.Item
          name="name"
          label="Tên ngành nghề"
          rules={[{ required: true, message: 'Vui lòng nhập tên ngành nghề!' }]}
        >
          <Input placeholder="Nhập tên ngành nghề" />
        </Form.Item>

        <Form.Item
          name="description"
          label="Mô tả"
        >
          <TextArea rows={4} placeholder="Nhập mô tả" />
        </Form.Item>

        <Form.Item
          name="status"
          label="Trạng thái"
          valuePropName="checked"
        >
          <Input type="checkbox" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

function IndustryList() {
  const [data, setData] = useState({ items: [], total: 0, currentPage: 1, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    limit: 10,
    search: '',
    status: undefined
  });
  const resizeTimeoutRef = useRef(null);
  const { token } = theme.useToken();

  const fetchIndustries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] !== undefined && queryParams[key] !== '') {
          params.append(key, queryParams[key]);
        }
      });
      
      const response = await axiosInstance.get(`${INDUSTRY_API.LIST}?${params.toString()}`);
      setData({
        items: response.data.industries || [],
        total: response.data.total || 0,
        currentPage: response.data.currentPage || 1,
        totalPages: response.data.totalPages || 1
      });
    } catch (error) {
      message.error('Không thể tải danh sách ngành nghề');
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    fetchIndustries();
  }, [fetchIndustries]);

  const handleResize = useCallback(() => {
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
    resizeTimeoutRef.current = setTimeout(() => {
      setWindowWidth(window.innerWidth);
    }, 200);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const isMobile = windowWidth <= 768;

  const columns = [
    {
      title: 'Mã ngành nghề',
      dataIndex: 'IndustryID',
      key: 'IndustryID',
      width: isMobile ? 120 : 150,
      fixed: 'left',
    },
    {
      title: 'Tên ngành nghề',
      dataIndex: 'IndustryName',
      key: 'IndustryName',
      width: isMobile ? 140 : 200,
      ellipsis: true,
    },
    {
      title: 'Mô tả',
      dataIndex: 'Description',
      key: 'Description',
      width: isMobile ? 160 : 300,
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'Status',
      key: 'Status',
      width: isMobile ? 100 : 120,
      render: status => (
        <span style={{ color: status ? '#52c41a' : '#ff4d4f' }}>
          {status ? 'Hoạt động' : 'Không hoạt động'}
        </span>
      ),
    },
    {
      title: 'Thao tác',
      key: 'action',
      fixed: 'right',
      width: isMobile ? 80 : 100,
      align: 'center',
      render: (_, record) => (
        <Space size={isMobile ? 4 : 8} style={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size={isMobile ? "small" : "middle"}
            style={{ padding: isMobile ? '0 8px' : '4px 8px' }}
          >
            {!isMobile && "Sửa"}
          </Button>
          <Button 
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            size={isMobile ? "small" : "middle"}
            style={{ padding: isMobile ? '0 8px' : '4px 8px' }}
          >
            {!isMobile && "Xóa"}
          </Button>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingIndustry(null);
    setIsModalVisible(true);
  };

  const handleEdit = async (record) => {
    try {
      const response = await axiosInstance.get(INDUSTRY_API.DETAIL(record.IndustryID));
      setEditingIndustry(response.data);
      setIsModalVisible(true);
    } catch (error) {
      message.error('Không thể tải thông tin ngành nghề');
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa ngành nghề ${record.IndustryName}?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await axiosInstance.delete(INDUSTRY_API.DELETE(record.IndustryID));
          message.success('Xóa ngành nghề thành công!');
          fetchIndustries();
        } catch (error) {
          message.error('Có lỗi xảy ra khi xóa ngành nghề');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingIndustry) {
        await axiosInstance.put(INDUSTRY_API.UPDATE(editingIndustry.IndustryID), {
          IndustryName: values.name,
          Description: values.description,
          Status: values.status
        });
        message.success('Cập nhật ngành nghề thành công!');
      } else {
        await axiosInstance.post(INDUSTRY_API.CREATE, {
          IndustryName: values.name,
          Description: values.description,
          Status: values.status
        });
        message.success('Thêm ngành nghề thành công!');
      }
      setIsModalVisible(false);
      fetchIndustries();
    } catch (error) {
      message.error('Có lỗi xảy ra: ' + (error.response?.data?.message || error.message));
    }
  };

  return (
    <div style={{ 
      padding: '16px',
      backgroundColor: token.colorBgContainer
    }}>
      <Card 
        title={<h2 style={{ margin: 0 }}>Danh Sách Ngành Nghề</h2>}
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? "small" : "middle"}
          >
            {!isMobile && "Thêm ngành nghề mới"}
          </Button>
        }
        bodyStyle={{ padding: isMobile ? 8 : 16 }}
        bordered={false}
      >
        <Table 
          columns={columns} 
          dataSource={data.items}
          rowKey="id"
          scroll={{ 
            x: isMobile ? 600 : 800
          }}
          pagination={{
            current: data.currentPage,
            pageSize: queryParams.limit,
            total: data.total,
            showSizeChanger: true,
            showQuickJumper: !isMobile,
            showTotal: (total) => `Tổng số ${total} bản ghi`,
            onChange: (page, pageSize) => {
              setQueryParams(prev => ({
                ...prev,
                page: page,
                limit: pageSize
              }));
            }
          }}
          loading={loading}
          size={isMobile ? "small" : "middle"}
        />
      </Card>
      <IndustryForm
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={editingIndustry}
        title={editingIndustry ? 'Sửa thông tin ngành nghề' : 'Thêm ngành nghề mới'}
      />
    </div>
  );
}

export default IndustryList; 